/* DASH-CUSTOM-02 — DASHBOARD LAYOUT CONFIG V1
 * ============================================================================
 * Schema, normalizzazione, migrazione, authorization model e optimistic
 * concurrency. Tutte funzioni PURE e testabili: nessun accesso a rete, storage
 * o DOM. In DASH-CUSTOM-02 nessuna di queste funzioni e' usata dal frontend.
 *
 * FORMATO CANONICO V1
 * ---------------------------------------------------------------------------
 *   { "version": 1,
 *     "order":   ["welcome", "kpi", ... ],   // lista di CHIAVI, non indici
 *     "widgets": { "welcome": { "enabled": true }, ... } }
 *
 * PERCHE' `order` e' una lista di chiavi e non di indici: se un widget viene
 * rimosso dal registry l'elenco si accorcia senza rinumerare (audit §14 Caso B);
 * con gli indici ogni config personale si romperebbe.
 *
 * `updated_at` NON fa parte dello schema di layout: serve alla sola optimistic
 * concurrency (D-07). E' tenuto FUORI dal layout canonico e restituito come
 * metadata esterno (`revision`). Cosi' il layout serializzato resta deterministico
 * e il token puo' cambiare senza invalidare la config.
 *
 * ASSENTI DI VOLONTA' (D-02): x, y, w, h, span, settings, role, user_id.
 *   La V2 potra' introdurre `span`; non e' implementata e non va anticipata.
 */

import { WIDGET_REGISTRY_V1, DASHBOARD_LAYOUT_SCHEMA_VERSION } from './widgetRegistry';
import { getDefaultForRole, DEFAULT_ROLE_MANAGER } from './dashboardDefaults';

/** Limite difensivo sulla dimensione del payload personale letto. */
export const MAX_LAYOUT_PAYLOAD_CHARS = 8192;

/** LABEL di persistenza previsto per il layout personale.
 *  STEP 1 ha classificato la capacita' di CORE_OPZIONI_UTENTE come UNSAFE
 *  (VALORE VARCHAR(255)): questo LABEL e' RISERVATO, non ancora utilizzabile. */
export const DASHBOARD_LAYOUT_LABEL = 'DASHBOARD_LAYOUT';

function isPlainObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/* ==========================================================================
 * MIGRAZIONE (STEP 8)
 * ==========================================================================
 * Esiste SOLO la versione 1. Non si creano versioni fittizie.
 *   - V1 -> V1 : identita'.
 *   - versione > CURRENT : NON si interpreta in modo arbitrario; si rifiuta,
 *     perche' inventare un significato produrrebbe silenziosamente un layout
 *     sbagliato (failure mode 12: mismatch frontend/backend).
 *   - versione assente/non numerica: provenienza ignota, si normalizza sulla V1.
 * La struttura e' gia' predisposta per V1 -> V2: bastera' un case in MIGRATIONS.
 */

export const MIGRATION_OUTCOME = Object.freeze({
    IDENTITY: 'IDENTITY',
    MIGRATED: 'MIGRATED',
    UNSUPPORTED_FUTURE_VERSION: 'UNSUPPORTED_FUTURE_VERSION',
    UNKNOWN_VERSION: 'UNKNOWN_VERSION',
});

/**
 * Migra dalla versione `fromVersion` alla versione corrente.
 * @returns {{outcome:string, config:Object|null}}
 */
export function migrateDashboardLayout(config, fromVersion) {
    const CURRENT = DASHBOARD_LAYOUT_SCHEMA_VERSION;
    const src = isPlainObject(config) ? config : {};
    const from = Number(fromVersion);

    if (!Number.isInteger(from) || from < 1) {
        return { outcome: MIGRATION_OUTCOME.UNKNOWN_VERSION, config: src };
    }
    if (from === CURRENT) {
        return { outcome: MIGRATION_OUTCOME.IDENTITY, config: src };
    }
    if (from > CURRENT) {
        return { outcome: MIGRATION_OUTCOME.UNSUPPORTED_FUTURE_VERSION, config: null };
    }
    /* Punto di innesto V1 -> V2 (non implementato: la V2 non esiste). */
    return { outcome: MIGRATION_OUTCOME.IDENTITY, config: src };
}

/* ==========================================================================
 * NORMALIZZAZIONE (STEP 7)
 * ==========================================================================
 * Principio: DEFAULT -> overlay personale valido -> sanitizzazione ->
 * authorization -> config canonica.
 *
 * DETERMINISMO: stesso input => stesso output. Nessuna randomicita', nessun
 * orologio, nessun accesso esterno.
 *
 * La funzione e' TOTALE: non lancia mai e, per QUALSIASI input (JSON corrotto,
 * oggetto estraneo, stringa enorme), produce una config canonica valida.
 */

function isStrictBoolean(v) {
    return typeof v === 'boolean';
}

/**
 * @param {Object} opts
 * @param {Object} [opts.defaultConfig]  default del ruolo; fallback MANAGER
 * @param {*}      [opts.rawConfig]      config personale grezza (qualsiasi)
 * @param {Array}  [opts.authorizedKeys] chiavi autorizzate; assenti = tutte
 * @param {Object} [opts.registry]       registry da usare
 * @returns {{config:Object, authorized:Array, dropped:Array, added:Array,
 *            migration:string, reasons:Array}}
 */
export function normalizeDashboardLayout(opts) {
    const registry = (opts && opts.registry) || WIDGET_REGISTRY_V1;
    const defaultConfig = (opts && opts.defaultConfig)
        || getDefaultForRole(DEFAULT_ROLE_MANAGER)
        || { version: DASHBOARD_LAYOUT_SCHEMA_VERSION, order: [], widgets: {} };

    const reasons = [];
    const dropped = [];
    const added = [];

    /* ---- 0. normalizzazione del default: fonte delle chiavi valide ---- */
    const defaultKeys = [];
    if (Array.isArray(defaultConfig.order)) {
        defaultConfig.order.forEach((k) => {
            if (registry.has(k) && defaultKeys.indexOf(k) === -1) defaultKeys.push(k);
        });
    }
    if (defaultKeys.length === 0) {
        registry.keys().forEach((k) => defaultKeys.push(k));
        reasons.push('DEFAULT_EMPTY');
    }

    /* ---- 1. overlay personale grezzo, parsing difensivo ---- */
    let raw = opts ? opts.rawConfig : null;
    if (typeof raw === 'string') {
        if (raw.length > MAX_LAYOUT_PAYLOAD_CHARS) {
            reasons.push('PAYLOAD_TOO_LARGE');
            raw = null;
        } else if (raw.trim() === '') {
            reasons.push('RAW_EMPTY');
            raw = null;
        } else {
            try {
                raw = JSON.parse(raw);
            } catch (e) {
                reasons.push('JSON_INVALID');
                raw = null;
            }
        }
    }
    if (raw === null || raw === undefined) {
        reasons.push('CONFIG_ABSENT');
    } else if (!isPlainObject(raw)) {
        /* casi 3, 11, 12, 20: struttura non valida -> si riparte dal default */
        reasons.push('CONFIG_SHAPE_INVALID');
        raw = null;
    }

    /* ---- 2. versione + migrazione (casi 4, 5, 6, 10) ---- */
    let migration = MIGRATION_OUTCOME.IDENTITY;
    if (isPlainObject(raw)) {
        if (typeof raw.version !== 'number' || !Number.isInteger(raw.version)) {
            reasons.push('VERSION_MISSING');
            migration = MIGRATION_OUTCOME.UNKNOWN_VERSION;
        } else if (raw.version > DASHBOARD_LAYOUT_SCHEMA_VERSION) {
            /* caso 12: mismatch. Si rifiuta invece di indovinare. */
            reasons.push('VERSION_FUTURE');
            migration = MIGRATION_OUTCOME.UNSUPPORTED_FUTURE_VERSION;
            raw = null;
        } else if (raw.version !== DASHBOARD_LAYOUT_SCHEMA_VERSION) {
            const m = migrateDashboardLayout(raw, raw.version);
            migration = m.outcome;
            raw = m.config;
        }
    }

    /* ---- 3. lettura difensiva di order/widgets ---- */
    const rawOrder = isPlainObject(raw) && Array.isArray(raw.order) ? raw.order : null;
    if (isPlainObject(raw) && raw.order !== undefined && raw.order !== null
        && !Array.isArray(raw.order)) {
        reasons.push('ORDER_NOT_ARRAY');
    }
    const rawWidgets = isPlainObject(raw) && isPlainObject(raw.widgets) ? raw.widgets : null;
    if (isPlainObject(raw) && raw.widgets !== undefined && raw.widgets !== null
        && !isPlainObject(raw.widgets)) {
        reasons.push('WIDGETS_NOT_OBJECT');
    }

    /* ---- 4. AUTHORIZATION: intersezione, mai unione (STEP 9) ----
     * Una config personale NON puo' AUMENTARE l'insieme autorizzato, solo
     * restringerlo. Se authorizedKeys non e' fornito si assume "tutto
     * autorizzato": nella fase pre-permissioni nessuna riga SHARED_FUNZIONALITA
     * esiste ancora. */
    const allKeys = registry.keys();
    const authorized = Array.isArray(opts && opts.authorizedKeys)
        ? allKeys.filter((k) => opts.authorizedKeys.indexOf(k) !== -1)
        : allKeys.slice();
    const authorizedSet = new Set(authorized);

    /* ---- 5. ordine canonico ---- */
    const order = [];
    const seen = new Set();

    /* 5a. ordine personale: solo chiavi valide, autorizzate, non duplicate
     * (casi 7 sconosciuta, 8 duplicato, 13 rimossa dal registry,
     *  15/16 non autorizzata, 19 chiave non stringa). */
    if (rawOrder) {
        rawOrder.forEach((k) => {
            if (typeof k !== 'string') {
                dropped.push(k);
                reasons.push('ORDER_KEY_NOT_STRING');
                return;
            }
            if (!registry.has(k)) {
                dropped.push(k);
                reasons.push('WIDGET_UNKNOWN');
                return;
            }
            if (seen.has(k)) {
                dropped.push(k);
                reasons.push('WIDGET_DUPLICATE');
                return;
            }
            if (!authorizedSet.has(k)) {
                dropped.push(k);
                reasons.push('WIDGET_UNAUTHORIZED');
                return;
            }
            seen.add(k);
            order.push(k);
        });
    }

    /* 5b. merge col default: widget assenti dall'ordine personale. In V1
     * coincide con l'ordine di DEFAULT_MANAGER_V1, quindi un utente senza
     * personalizzazione ottiene esattamente l'ordine di default. */
    defaultKeys.forEach((k) => {
        if (seen.has(k)) return;
        if (!authorizedSet.has(k)) {
            dropped.push(k);
            reasons.push('WIDGET_UNAUTHORIZED');
            return;
        }
        seen.add(k);
        order.push(k);
        added.push(k);
    });

    /* 5c. chiavi autorizzate note al registry ma assenti dal default: entrano
     * in coda, cosi' un widget nuovo non viene perso (caso 14).
     * ATTENZIONE: anche questo passaggio deve rispettare l'autorizzazione,
     * altrimenti un widget non autorizzato verrebbe reintrodotto qui. */
    allKeys.forEach((k) => {
        if (seen.has(k)) return;
        if (!authorizedSet.has(k)) { dropped.push(k); reasons.push('WIDGET_UNAUTHORIZED'); return; }
        seen.add(k);
        order.push(k);
        added.push(k);
        reasons.push('WIDGET_NOT_IN_DEFAULT');
    });

    /* ---- 6. widgets canonici ---- */
    const widgets = {};
    order.forEach((k) => {
        const def = defaultConfig.widgets && defaultConfig.widgets[k];
        let enabled = def && isStrictBoolean(def.enabled) ? def.enabled : true;
        const cfg = rawWidgets ? rawWidgets[k] : undefined;
        if (cfg !== undefined) {
            if (!isPlainObject(cfg)) {
                reasons.push('WIDGET_ENTRY_INVALID');
            } else if ('enabled' in cfg) {
                if (isStrictBoolean(cfg.enabled)) {
                    enabled = cfg.enabled;
                } else {
                    /* valore non booleano es. 'true' o 1: rifiutato, si tiene
                     * il default del widget (caso 10). */
                    reasons.push('ENABLED_NOT_BOOLEAN');
                }
            }
        }
        widgets[k] = { enabled };
    });

    return {
        config: {
            version: DASHBOARD_LAYOUT_SCHEMA_VERSION,
            order,
            widgets,
        },
        authorized,
        dropped,
        added,
        migration,
        reasons,
    };
}

/* ==========================================================================
 * AUTHORIZATION MODEL (STEP 9)
 * ==========================================================================
 *   AUTHORIZED = permessi del ruolo  INTERSECT  chiavi del registry
 *   ENABLED    = AUTHORIZED  INTERSECT  preferenza personale
 *   RENDERED   = ENABLED
 *
 * Vincoli espliciti, verificati dai test:
 *   - la configurazione personale puo' solo RESTRINGERE, mai ampliare;
 *   - il frontend NON e' fonte di autorizzazione: qui si incrocia un insieme
 *     autorizzato GIA' calcolato lato server, mai un flag del client;
 *   - non esiste e non viene inventato alcun v-if di sicurezza: la visibilita'
 *     non e' un controllo di accesso.
 *
 * In DASH-CUSTOM-02 NON viene introdotto alcun permesso DB e il payload
 * dashboard NON viene modificato. Queste funzioni sono il contratto che una
 * fase futura applichera'.
 */

/** Chiavi autorizzate: intersezione fra permessi e registry. */
export function computeAuthorizedWidgets(opts) {
    const registry = (opts && opts.registry) || WIDGET_REGISTRY_V1;
    const allowed = Array.isArray(opts && opts.authorizedKeys)
        ? new Set(opts.authorizedKeys)
        : null;
    return registry.keys().filter((k) => (allowed ? allowed.has(k) : true));
}

/**
 * Widget effettivamente renderizzati: si segue l'ordine CANONICO e si
 * applica l'intersezione con i widget autorizzati. Un widget abilitato ma non
 * autorizzato non compare MAI nell'output.
 */
export function computeRenderedWidgets(authorizedKeys, config) {
    const out = [];
    if (!Array.isArray(authorizedKeys)) return out;
    const allowed = new Set(authorizedKeys);
    if (!isPlainObject(config) || !isPlainObject(config.widgets)) return out;
    const order = Array.isArray(config.order) ? config.order : Object.keys(config.widgets);
    order.forEach((k) => {
        if (!allowed.has(k)) return;
        const w = config.widgets[k];
        if (w && w.enabled === true) out.push(k);
    });
    return out;
}

/* ==========================================================================
 * OPTIMISTIC CONCURRENCY (STEP 11, D-07)
 * ==========================================================================
 * Nessuna colonna DB e' stata inventata. CORE_OPZIONI_UTENTE non ha un campo
 * `updated_at` (VALORE e' VARCHAR(255), PK autoincrement ID_OPZIONE), quindi
 * il token di revisione e' DERIVATO in modo deterministico dal contenuto
 * canonico: stesso contenuto => stesso token, contenuto diverso => token diverso.
 *
 * Il token calcola la serializzazione STABILE del layout canonico (chiavi
 * nell'ordine fisso, senza updated_at): non dipende dall'ordinamento di
 * JSON.stringify ed e' riproducibile sia in JS sia in PHP.
 *
 * Contratto del futuro SAVE:
 *     se currentRevision !== expectedRevision  ->  CONFLICT, NESSUNA scrittura.
 * Nessun last-write-wins silenzioso.
 *
 * NOTA: il token serve SOLO a rilevare conflitti fra sessioni. Non e' un hash
 * crittografico e non protegge da manomissione: l'autorizzazione resta un
 * problema server-side, risolto come da audit §18.
 */

export const CONCURRENCY_OUTCOME = Object.freeze({
    OK: 'OK',
    CONFLICT: 'CONFLICT',
});

/** Serializzazione canonica e stabile del layout. */
export function serializeCanonical(config) {
    const c = isPlainObject(config) ? config : {};
    const order = Array.isArray(c.order) ? c.order : [];
    const widgets = isPlainObject(c.widgets) ? c.widgets : {};
    const keys = order.map((k) => String(k));
    const widgetsJson = keys.map((k) => {
        const w = isPlainObject(widgets[k]) ? widgets[k] : {};
        return JSON.stringify(k) + ':{"enabled":' + (w.enabled === true ? 'true' : 'false') + '}';
    }).join(',');
    return '{"version":' + DASHBOARD_LAYOUT_SCHEMA_VERSION
        + ',"order":[' + keys.map((k) => JSON.stringify(k)).join(',') + ']'
        + ',"widgets":{' + widgetsJson + '}}';
}

/**
 * Token di revisione deterministico (FNV-1a 32 bit).
 * Nessuna dipendenza esterna; algoritmo replicabile in PHP.
 */
export function computeRevisionToken(config) {
    const s = serializeCanonical(config);
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return 'rev_' + ('00000000' + h.toString(16)).slice(-8);
}

/**
 * Verifica di concorrenza per il futuro SAVE.
 * @param {string|null} expectedRevision revisione ricevuta dal client
 * @param {Object} currentConfig config canonica attualmente persistita
 * @returns {{outcome:string, currentRevision:string}}
 */
export function checkRevision(expectedRevision, currentConfig) {
    const current = computeRevisionToken(currentConfig);
    if (expectedRevision === null || expectedRevision === undefined || expectedRevision === '') {
        /* Nessuna revisione dichiarata: il futuro endpoint dovra' comunque
         * validare il contenuto. Non si inventa un conflitto. */
        return { outcome: CONCURRENCY_OUTCOME.OK, currentRevision: current };
    }
    if (expectedRevision !== current) {
        return { outcome: CONCURRENCY_OUTCOME.CONFLICT, currentRevision: current };
    }
    return { outcome: CONCURRENCY_OUTCOME.OK, currentRevision: current };
}

export default {
    MAX_LAYOUT_PAYLOAD_CHARS,
    DASHBOARD_LAYOUT_LABEL,
    MIGRATION_OUTCOME,
    CONCURRENCY_OUTCOME,
    migrateDashboardLayout,
    normalizeDashboardLayout,
    computeAuthorizedWidgets,
    computeRenderedWidgets,
    serializeCanonical,
    computeRevisionToken,
    checkRevision,
};